<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">Shortcuts</h1>
                </div>
                <div class="stage-page-header-actions">
                    <button class="btn-stage-icon-plain" title="Shortcut hinzufügen" type="button" @click="addItem">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </button>
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" to="/workspace/admin/settings">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                </div>
            </div>
        </div>

        <div class="stage-page-gutters stage-page-fill relative pt-3 h-0">
            <form class="w-full h-full" @submit.prevent="save">
                <div class="w-full h-full bg-white shadow sm:rounded border border-ultralight overflow-hidden flex flex-col min-h-0">
                    <loading :status="loading"/>

                    <div class="flex items-stretch flex-1 min-h-0">
                        <div class="flex flex-col flex-auto min-w-0 overflow-hidden">
                            <div class="xl:hidden border-b border-ultralight px-4 py-4 space-y-4 bg-white">
                                <div class="rounded-lg border border-bordergray-100 bg-stage-background px-5 py-5">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">Leader-Modus</h3>
                                    <p class="mt-2 text-sm leading-6 text-gray-500">
                                        Die Navigation startet durch kurzes Antippen von <code>Cmd</code> auf dem Mac bzw. <code>Ctrl</code> unter Windows oder Linux.
                                    </p>
                                </div>

                                <div class="rounded-lg border border-bordergray-100 bg-white px-5 py-5">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="navigation-shortcuts-enabled-mobile">
                                        Shortcuts global aktiv
                                    </label>
                                    <input-switch
                                        id="navigation-shortcuts-enabled-mobile"
                                        v-model="enabled"
                                        :disabled-label="'Leader-Navigation ist deaktiviert'"
                                        :enabled-label="'Leader-Navigation ist aktiviert'"
                                    ></input-switch>
                                </div>

                                <div class="rounded-lg border border-bordergray-100 bg-stage-background px-5 py-5">
                                    <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Ablauf</div>
                                    <div class="mt-3 flex items-center gap-2 text-sm text-gray-700">
                                        <span class="inline-flex min-w-[3.5rem] items-center justify-center rounded-full border border-bordergray-100 bg-white px-3 py-1 font-semibold shadow-sm">
                                            {{ leaderLabel }}
                                        </span>
                                        <span>antippen</span>
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-right"></svg-vue>
                                        <span>Buchstabe druecken</span>
                                    </div>
                                </div>
                            </div>

                            <div class="flex-1 min-h-0 overflow-auto">
                                <div v-if="items.length === 0" class="flex h-full items-center justify-center px-6 py-10">
                                    <empty-state
                                        title="Noch keine Shortcuts hinterlegt."
                                    />
                                </div>

                                <template v-else>
                                    <div class="hidden lg:block overflow-hidden">
                                        <div class="align-middle inline-block min-w-full">
                                            <table class="table-listview table-listview--zebra navigation-shortcuts-table">
                                                    <thead>
                                                    <tr>
                                                        <th class="table-listview-l-th w-44">Taste</th>
                                                        <th class="table-listview-l-th w-44">Zieltyp</th>
                                                        <th class="table-listview-l-th">Ziel</th>
                                                        <th class="table-listview-l-th w-56">Notiz</th>
                                                        <th class="table-listview-l-th w-28">Aktiv</th>
                                                        <th class="table-listview-l-th table-listview-l-th-right w-24">Aktion</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody class="bg-white">
                                                    <tr v-for="(item, index) in items" :key="item.uid">
                                                        <td class="table-listview-l-td align-middle">
                                                            <div class="flex items-center">
                                                            <input
                                                                :value="item.key ? getKeyDisplay(item.key) : ''"
                                                                class="form-input block w-full text-sm"
                                                                placeholder="Taste druecken"
                                                                    readonly
                                                                    type="text"
                                                                    @keydown.prevent.stop="captureKey($event, index)"
                                                                >
                                                            </div>
                                                        </td>
                                                        <td class="table-listview-l-td align-middle">
                                                            <select
                                                                v-model="item.target_type"
                                                                class="form-select block w-full text-sm"
                                                                @change="handleTargetTypeChange(item)"
                                                            >
                                                                <option value="menu">Menueziel</option>
                                                                <option value="route">Relative URL</option>
                                                            </select>
                                                        </td>
                                                        <td class="table-listview-l-td align-middle">
                                                            <select
                                                                v-if="item.target_type === 'menu'"
                                                                v-model="item.target_value"
                                                                class="form-select block w-full text-sm"
                                                            >
                                                                <option value="">Bitte wählen</option>
                                                                <option
                                                                    v-for="target in menuTargets"
                                                                    :key="item.uid + '-' + target.id"
                                                                    :value="target.id"
                                                                >
                                                                    {{ target.label }}
                                                                </option>
                                                            </select>
                                                            <input
                                                                v-else
                                                                v-model="item.target_value"
                                                                class="form-input block w-full text-sm"
                                                                placeholder="/workspace/tickets"
                                                                type="text"
                                                            >
                                                        </td>
                                                        <td class="table-listview-l-td align-middle">
                                                            <input
                                                                v-model="item.label"
                                                                class="form-input block w-full text-sm"
                                                                maxlength="255"
                                                                placeholder="Optionale Notiz"
                                                                type="text"
                                                            >
                                                        </td>
                                                        <td class="table-listview-l-td align-middle">
                                                            <label class="inline-flex items-center">
                                                                <input
                                                                    v-model="item.enabled"
                                                                    class="form-checkbox h-4 w-4 text-gray-700"
                                                                    type="checkbox"
                                                                >
                                                                <span class="ml-2 text-sm text-gray-600">Aktiv</span>
                                                            </label>
                                                        </td>
                                                        <td class="table-listview-l-td align-middle text-right">
                                                            <button
                                                                aria-label="Shortcut entfernen"
                                                                class="btn-stage-icon-plain ml-auto"
                                                                title="Shortcut entfernen"
                                                                type="button"
                                                                @click="removeItem(index)"
                                                            >
                                                                <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div class="lg:hidden space-y-4 px-4 py-4">
                                            <div
                                                v-for="(item, index) in items"
                                                :key="'mobile-' + item.uid"
                                                class="rounded-lg border border-bordergray-100 bg-white shadow-sm"
                                            >
                                                <div class="border-b border-ultralight px-4 py-3">
                                                    <div class="flex items-center justify-between gap-3">
                                                        <div>
                                                            <div class="text-sm font-semibold text-gray-800">
                                                                {{ getLeaderCombination(item.key) }}
                                                            </div>
                                                            <div class="mt-1 text-xs text-gray-500">
                                                                {{ item.label || 'Ohne Notiz' }}
                                                            </div>
                                                        </div>
                                                        <button
                                                            aria-label="Shortcut entfernen"
                                                            class="btn-stage-icon-plain"
                                                            title="Shortcut entfernen"
                                                            type="button"
                                                            @click="removeItem(index)"
                                                        >
                                                            <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div class="grid grid-cols-1 gap-4 px-4 py-4">
                                                    <div>
                                                        <label class="block text-sm font-medium leading-5 text-gray-700">Taste</label>
                                                        <div class="mt-1 flex items-center">
                                                            <input
                                                                :value="item.key ? getKeyDisplay(item.key) : ''"
                                                                class="form-input block w-full text-sm"
                                                                placeholder="Taste druecken"
                                                                readonly
                                                                type="text"
                                                                @keydown.prevent.stop="captureKey($event, index)"
                                                            >
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label class="block text-sm font-medium leading-5 text-gray-700">Zieltyp</label>
                                                        <select
                                                            v-model="item.target_type"
                                                            class="form-select mt-1 block w-full text-sm"
                                                            @change="handleTargetTypeChange(item)"
                                                        >
                                                            <option value="menu">Menueziel</option>
                                                            <option value="route">Relative URL</option>
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label class="block text-sm font-medium leading-5 text-gray-700">Ziel</label>
                                                        <select
                                                            v-if="item.target_type === 'menu'"
                                                            v-model="item.target_value"
                                                            class="form-select mt-1 block w-full text-sm"
                                                        >
                                                            <option value="">Bitte wählen</option>
                                                            <option
                                                                v-for="target in menuTargets"
                                                                :key="'mobile-' + item.uid + '-' + target.id"
                                                                :value="target.id"
                                                            >
                                                                {{ target.label }}
                                                            </option>
                                                        </select>
                                                        <input
                                                            v-else
                                                            v-model="item.target_value"
                                                            class="form-input mt-1 block w-full text-sm"
                                                            placeholder="/workspace/tickets"
                                                            type="text"
                                                        >
                                                    </div>

                                                    <div>
                                                        <label class="block text-sm font-medium leading-5 text-gray-700">Notiz</label>
                                                        <input
                                                            v-model="item.label"
                                                            class="form-input mt-1 block w-full text-sm"
                                                            maxlength="255"
                                                            placeholder="Optionale Notiz"
                                                            type="text"
                                                        >
                                                    </div>

                                                    <div class="flex items-center justify-between">
                                                        <label class="inline-flex items-center">
                                                            <input
                                                                v-model="item.enabled"
                                                                class="form-checkbox h-4 w-4 text-gray-700"
                                                                type="checkbox"
                                                            >
                                                            <span class="ml-2 text-sm text-gray-600">Aktiv</span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                    </div>
                                </template>
                            </div>
                        </div>

                        <aside
                            :style="{flex: '0 0 352px', width: '352px'}"
                            class="hidden xl:block border-l border-ultralight"
                        >
                            <div class="h-full overflow-y-auto bg-white">
                                <div class="px-6 py-6 border-b border-ultralight">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">Leader-Modus</h3>
                                    <p class="mt-2 text-sm leading-6 text-gray-500">
                                        Die Navigation startet durch kurzes Antippen von <code>Cmd</code> auf dem Mac bzw. <code>Ctrl</code> unter Windows oder Linux.
                                    </p>
                                </div>

                                <div class="px-6 py-6 border-b border-ultralight">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="navigation-shortcuts-enabled">
                                        Shortcuts global aktiv
                                    </label>
                                    <input-switch
                                        id="navigation-shortcuts-enabled"
                                        v-model="enabled"
                                        :disabled-label="'Leader-Navigation ist deaktiviert'"
                                        :enabled-label="'Leader-Navigation ist aktiviert'"
                                    ></input-switch>
                                    <div class="mt-3 text-xs leading-5 text-gray-500">
                                        Ist die Funktion deaktiviert, bleiben alle Zuordnungen gespeichert, werden aber nicht ausgewertet.
                                    </div>
                                </div>

                                <div class="px-6 py-6">
                                    <div class="text-xs tracking-wide text-gray-500 mb-3">Ablauf</div>
                                    <div class="flex items-center gap-2 text-sm text-gray-700">
                                        <span class="inline-flex min-w-[3.5rem] items-center justify-center rounded-full border border-bordergray-100 bg-white px-3 py-1 font-semibold shadow-sm">
                                            {{ leaderLabel }}
                                        </span>
                                        <span>antippen</span>
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-right"></svg-vue>
                                        <span>Buchstabe druecken</span>
                                    </div>
                                    <div class="mt-3 text-xs leading-5 text-gray-500">
                                        Wählt ein Nutzer ein Ziel ohne Berechtigung, wird der Shortcut still ignoriert. In Formularen, Editoren und Dialogen greift der Modus nicht.
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>

                    <div class="stage-footer-surface px-4 py-3 sm:px-6 flex items-center justify-between border-t border-ultralight">
                        <div class="text-xs text-gray-500">
                            {{ items.length }} Shortcut{{ items.length === 1 ? '' : 's' }} konfiguriert
                        </div>
                        <span class="inline-flex items-center">
                            <button class="btn-stage-secondary mr-4" type="button" @click="addItem">
                                Shortcut hinzufügen
                            </button>
                            <button class="btn-stage-primary" type="submit">
                                {{ $t('Save settings') }}
                            </button>
                        </span>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import {
    NAVIGATION_SHORTCUT_MENU_TARGETS,
    getNavigationShortcutKeyDisplayLabel,
    getNavigationShortcutLeaderLabel,
    normalizeNavigationShortcutConfig,
    normalizeNavigationShortcutKey,
    normalizeRelativeRoute,
    shortcutKeyFromKeyboardEvent,
} from "@/utils/navigation-shortcuts";

export default {
    name: "shortcuts",
    data() {
        return {
            loading: true,
            enabled: true,
            items: [],
            menuTargets: NAVIGATION_SHORTCUT_MENU_TARGETS,
            nextUid: 1,
        };
    },
    computed: {
        leaderLabel() {
            return getNavigationShortcutLeaderLabel();
        },
    },
    mounted() {
        this.get();
    },
    methods: {
        createItem(item = {}) {
            const key = normalizeNavigationShortcutKey(item.key) || '';
            const targetType = item.target_type === 'route' ? 'route' : 'menu';
            const targetValue = targetType === 'route'
                ? (normalizeRelativeRoute(item.target_value) || '')
                : String(item.target_value || '');

            const entry = {
                uid: this.nextUid++,
                enabled: item.enabled === undefined ? true : !!item.enabled,
                label: String(item.label || ''),
                key,
                target_type: targetType,
                target_value: targetValue,
            };

            if (entry.target_type === 'menu' && entry.target_value === '' && this.menuTargets.length > 0) {
                entry.target_value = this.menuTargets[0].id;
            }

            return entry;
        },
        addItem() {
            this.items.push(this.createItem({
                enabled: true,
                label: '',
                key: '',
                target_type: 'menu',
                target_value: this.menuTargets.length > 0 ? this.menuTargets[0].id : '',
            }));
        },
        removeItem(index) {
            this.items.splice(index, 1);
        },
        captureKey(event, index) {
            if (!this.items[index]) {
                return;
            }

            if (event.key === 'Backspace' || event.key === 'Delete' || event.key === 'Escape') {
                this.items[index].key = '';
                return;
            }

            const normalizedKey = shortcutKeyFromKeyboardEvent(event);
            if (!normalizedKey) {
                return;
            }

            this.items[index].key = normalizedKey;
        },
        handleTargetTypeChange(item) {
            if (!item) {
                return;
            }

            item.target_value = item.target_type === 'route'
                ? ''
                : (this.menuTargets.length > 0 ? this.menuTargets[0].id : '');
        },
        getKeyDisplay(key) {
            return getNavigationShortcutKeyDisplayLabel(key);
        },
        getLeaderCombination(key) {
            if (!key) {
                return this.leaderLabel;
            }

            return this.leaderLabel + ' ' + this.getKeyDisplay(key);
        },
        get() {
            const self = this;
            self.loading = true;

            axios.get('api/workspace/admin/settings/shortcuts').then(function (response) {
                const config = normalizeNavigationShortcutConfig(response.data || {});

                self.enabled = config.enabled !== false;
                self.items = config.items.map((item) => self.createItem(item));
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while loading data').toString(),
                    type: 'error'
                });
            });
        },
        save() {
            const self = this;
            const items = self.items.map((item) => ({
                enabled: item.enabled !== false,
                label: String(item.label || '').trim(),
                key: normalizeNavigationShortcutKey(item.key) || '',
                target_type: item.target_type === 'route' ? 'route' : 'menu',
                target_value: item.target_type === 'route'
                    ? (normalizeRelativeRoute(item.target_value) || '')
                    : String(item.target_value || '').trim(),
            })).filter((item) => item.key !== '');

            self.loading = true;

            axios.post('api/workspace/admin/settings/shortcuts', {
                enabled: self.enabled,
                items,
            }).then(function (response) {
                const config = normalizeNavigationShortcutConfig(response.data && response.data.config ? response.data.config : {
                    enabled: self.enabled,
                    items,
                });

                self.$store.commit('patchSettings', {
                    navigation_shortcuts: config,
                });

                self.enabled = config.enabled !== false;
                self.items = config.items.map((item) => self.createItem(item));
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
    },
};
</script>

<style scoped>
:deep(.navigation-shortcuts-table .table-listview-l-td) {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
}
</style>
